import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../../components/common/Panel';
import Wall from '../../components/common/Wall';
import DateRange from '../../components/common/DateRange';
import Button from '../../components/common/Button';
import { CountTopFilter } from '../../components/common/CountTopFilter';
import { getBrandOrders, setBrandOrdersLoading } from '../../store/actions/brandOrders';
import { setBrandOrdersRetailerFilter, setOrderLastDayFilter } from '../../store/actions/pageFilters';
import DropDownFilter from '../../components/filters/DropDownFilter';
import { BrandOrdersActions, countsDisplays } from '../../store/reducers/brandOrders';
import { supplierOrderReportWithCurrency } from '../../constants/featureFlag';
import { supplierRetailerUtils } from '../../utils/supplierRetailer';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

export function OrdersPagePanel({ onDownloadReport }) {
  const dispatch = useDispatch();
  const currencyFeature = useFeatureFlag(supplierOrderReportWithCurrency, false, true);
  const supplierRetailers = useSelector((state) => state.role?.supplier?.supplier_retailers ?? []);
  const { counts, loading, retailers } = useSelector((state) => state.brandOrders);
  const { currency: supplierCurrency, currencies } = useSelector((state) => state.role?.supplier || {});

  const shouldShowCurrencyTypeSelection = useMemo(() => {
    if (!currencyFeature || !currencies?.length) return false;
    return currencies.some((currency) => currency.toLowerCase() !== supplierCurrency.toLowerCase());
  }, [currencyFeature, currencies, supplierCurrency]);

  const channelDropdownSelectorOptions = useMemo(() => {
    return retailers.map((retailer) => {
      const retailerDisplayName = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
        supplierRetailers,
        retailer,
        retailerId: retailer.value,
      });
      return {
        label: retailerDisplayName || retailer.label,
        value: retailer.value,
      };
    });
  }, [retailers, supplierRetailers]);

  const handleChangeRange = (startDate, endDate) => {
    dispatch(setBrandOrdersLoading({ orders: false }));
    dispatch(setOrderLastDayFilter(startDate, endDate));
  };

  const handleClick = (count) => {
    dispatch(getBrandOrders(count));
  };

  const handleGenerateReportClick = () => {
    if (shouldShowCurrencyTypeSelection) {
      dispatch({ action: BrandOrdersActions.modals, payload: { currency: true } });
    } else {
      onDownloadReport();
    }
  };

  const disableGenerateReportButton = counts.canceled <= 0 && counts.completed <= 0 && counts.pending <= 0;

  return (
    <Panel>
      <Wall />
      <DateRange
        onChange={(startDate, endDate) => {
          handleChangeRange(startDate, endDate);
        }}
      />
      <Wall />
      {Object.entries(countsDisplays).map(([count, display]) => (
        <CountTopFilter
          key={count}
          name={count}
          onClick={handleClick}
          display={display}
          count={counts[count]}
          isActive={count === counts.active}
          loading={loading.filters}
        />
      ))}
      <Wall />
      <DropDownFilter
        currentIndex={0}
        values={channelDropdownSelectorOptions}
        onChange={(value) => dispatch(setBrandOrdersRetailerFilter(value))}
      />
      <Wall />
      <Button text="Generate Report" onClick={handleGenerateReportClick} disabled={disableGenerateReportButton} />
      <Wall />
    </Panel>
  );
}
